/** The analytics and ad tags a project or set can load on its pages. */
export const TRACKING_TOOLS = ["ga4", "meta", "linkedin"] as const;
export type TrackingTool = (typeof TRACKING_TOOLS)[number];

/** Each tool's name, its id's, and how that id looks, so a typo is caught before it ships. */
export const TRACKING_TOOL_INFO: Record<
  TrackingTool,
  {
    name: string;
    idLabel: string;
    placeholder: string;
    pattern: RegExp;
    /** What to set up on the tool's side. */
    hint?: string;
  }
> = {
  ga4: {
    name: "Google Analytics 4",
    idLabel: "Google Analytics 4 measurement ID",
    placeholder: "G-XXXXXXXXXX",
    pattern: /^G-[A-Z0-9]{4,20}$/,
    hint: "In GA4's enhanced measurement, turn off page changes from browser history: pages send their own page views.",
  },
  meta: {
    name: "Meta Pixel",
    idLabel: "Meta Pixel ID",
    placeholder: "123456789012345",
    pattern: /^\d{10,20}$/,
  },
  linkedin: {
    name: "LinkedIn Insight Tag",
    idLabel: "LinkedIn partner ID",
    placeholder: "1234567",
    pattern: /^\d{3,12}$/,
    hint: "LinkedIn counts page views itself; set up conversions in Campaign Manager by page address.",
  },
};

/** A project's or set's tag ids, by tool. */
export type TrackingIds = Partial<Record<TrackingTool, string | null>>;

/** Every id a page's tags load with, by tool; one source's ids each once. */
export type ResolvedTracking = Partial<Record<TrackingTool, string[]>>;

/** Whether `id` looks like an id of `tool`; empty passes, as no tag. */
export const isTrackingId = (tool: TrackingTool, id?: string | null) =>
  !id || TRACKING_TOOL_INFO[tool].pattern.test(id);

/** What to say when an id doesn't look like `tool`'s. */
export const trackingIdMessage = (tool: TrackingTool) =>
  `Enter a ${TRACKING_TOOL_INFO[tool].idLabel}, like ${TRACKING_TOOL_INFO[tool].placeholder}.`;

/**
 * A page's tags: its project's and its set's together, not one over the
 * other, so both owners measure the page. Null when neither has any.
 */
export function resolveTracking(
  ...sources: (TrackingIds | null | undefined)[]
): ResolvedTracking | null {
  const resolved: ResolvedTracking = {};
  for (const tool of TRACKING_TOOLS) {
    const ids = [
      ...new Set(
        sources
          .map((source) => source?.[tool])
          .filter((id): id is string => !!id && isTrackingId(tool, id))
      ),
    ];
    if (ids.length) resolved[tool] = ids;
  }
  return Object.keys(resolved).length ? resolved : null;
}

/** What a view of a page counts as, for the ad tools' conversion reports. */
export const CONVERSION_EVENTS = [
  "lead",
  "sign_up",
  "view_content",
  "custom",
] as const;
export type ConversionEvent = (typeof CONVERSION_EVENTS)[number];

export const CONVERSION_NAME_MAX_LENGTH = 40;

export interface PageConversion {
  event: ConversionEvent;
  /** The event's own name, for `custom`. */
  name?: string | null;
}
